import React, { useEffect, useMemo, useRef, useState } from 'react';
import { certifications } from '../data/certifications';
import { Footer } from '../components';
import SEO, { generateBreadcrumbSchema } from '../components/SEO';
import { useGSAPReveal } from '../util/useGSAPReveal';

const certificationsListSchema = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: 'Professional Certifications — Asim Hafeez',
  description:
    'Professional certifications demonstrating expertise in AI engineering, blockchain development, system design, cloud architecture, and modern technologies.',
  numberOfItems: certifications.length,
  itemListElement: certifications.map((cert, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: cert.title,
    description: cert.description,
  })),
};

const certificationsBreadcrumbs = generateBreadcrumbSchema([
  { name: 'Home', url: 'https://asimhafeez.com/' },
  { name: 'Certifications', url: 'https://asimhafeez.com/certifications' },
]);
const INITIAL_DISPLAY_COUNT = 6;
const DISPLAY_INCREMENT = 6;

const parseIssueDate = (issueDate: string) => {
  const parsed = new Date(`${issueDate} 1`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatIssueDate = (issueDate: string) => {
  const parsed = parseIssueDate(issueDate);

  return parsed
    ? parsed.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
    : issueDate;
};

const CertificationsPage: React.FC = () => {
  const [isMobileView, setIsMobileView] = useState(false);
  const [displayCount, setDisplayCount] = useState(INITIAL_DISPLAY_COUNT);
  const archiveRef = useRef<HTMLElement | null>(null);

  const headerRef = useGSAPReveal({ variant: 'fade-up', start: 'top 95%' });
  const spotlightRef = useGSAPReveal({ variant: 'fade-up', delay: 0.08, start: 'top 95%' });
  const listRef = useGSAPReveal({ variant: 'stagger-children', delay: 0.15, staggerAmount: 0.05, start: 'top 95%' });

  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobileView(window.innerWidth <= 768);
    };

    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);

    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const sortedCertifications = useMemo(() => {
    return [...certifications].sort((a, b) => {
      const left = parseIssueDate(a.issueDate)?.getTime() || 0;
      const right = parseIssueDate(b.issueDate)?.getTime() || 0;
      return right - left;
    });
  }, []);

  const featuredCertification = sortedCertifications[0];
  const visibleCertifications = sortedCertifications.slice(0, displayCount);
  const hasMoreCertifications = displayCount < sortedCertifications.length;
  const canShowLess = displayCount > INITIAL_DISPLAY_COUNT;

  const organizationCount = useMemo(
    () => new Set(certifications.map((cert) => cert.organization)).size,
    []
  );

  const skillCount = useMemo(() => {
    const skills = new Set<string>();
    certifications.forEach((cert) => cert.skills?.forEach((skill) => skills.add(skill)));
    return skills.size;
  }, []);

  const handleShowLess = () => {
    setDisplayCount(INITIAL_DISPLAY_COUNT);

    requestAnimationFrame(() => {
      archiveRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  return (
    <section
      className="certifications-page"
      style={{
        minHeight: '100vh',
        background: 'transparent',
        padding: isMobileView ? '92px 20px 0' : '132px 48px 0',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <SEO
        title="Professional Certifications — Asim Hafeez"
        description="25+ professional certifications by Asim Hafeez demonstrating expertise in AI engineering, agentic AI, blockchain development, system design, cloud architecture, and modern technologies. From IBM, MIT, AWS, Meta, and more."
        canonicalUrl="/certifications"
        keywords="asim hafeez certifications, AI certifications, blockchain certifications, system design credentials, cloud architecture certificates, professional achievements, IBM certifications, agentic AI, LangChain certification"
        jsonLd={[certificationsListSchema, certificationsBreadcrumbs]}
      />

      <div className="certifications-shell">
        <header ref={headerRef} className="certifications-hero">
          <div className="certifications-kicker">Learning archive</div>
          <div className="certifications-hero__row">
            <div className="certifications-hero__copy">
              <h1>My Certification Archive</h1>
              <p>
                A personal record of what I’ve studied across AI, system design, cloud,
                blockchain, and practical software engineering.
              </p>
            </div>

            <div className="certifications-hero__meta">
              <div className="certifications-hero__stat">
                <span>{certifications.length}+</span>
                <p>professional certifications</p>
              </div>
              <div className="certifications-hero__note">
                Built through programs from {organizationCount}+ organizations, spanning {skillCount}+ technical areas.
              </div>
            </div>
          </div>
        </header>

        {featuredCertification ? (
          <section ref={spotlightRef} className="certifications-spotlight">
            <div className="certifications-spotlight__image">
              {featuredCertification.certificateImage ? (
                <img
                  src={featuredCertification.certificateImage}
                  alt={`${featuredCertification.title} Certificate`}
                  loading="lazy"
                />
              ) : (
                <div className="certifications-certificate-fallback">Certificate preview</div>
              )}
            </div>

            <div className="certifications-spotlight__content">
              <div className="certifications-spotlight__topline">
                <span>{featuredCertification.organization}</span>
                <span>{formatIssueDate(featuredCertification.issueDate)}</span>
              </div>

              <h2>{featuredCertification.title}</h2>
              <p>{featuredCertification.description}</p>

              {featuredCertification.skills?.length ? (
                <div className="certifications-skill-strip">
                  {featuredCertification.skills.slice(0, 3).map((skill) => (
                    <span key={skill}>{skill}</span>
                  ))}
                </div>
              ) : null}
            </div>
          </section>
        ) : null}

        <section ref={archiveRef} className="certifications-archive">
          <div className="certifications-archive__header">
            <div>
              <div className="certifications-kicker">Browse archive</div>
              <h2>Certifications</h2>
            </div>
            <p>
              The archive is intentionally restrained so the titles, issuers, dates, and areas of focus stay easy to scan.
            </p>
          </div>

          <div ref={listRef} className="certifications-list">
            {visibleCertifications.map((cert, index) => (
              <article key={`${cert.title}-${cert.issueDate}-${index}`} className="certification-row">
                <div className="certification-row__body">
                  <div className="certification-row__topline">{cert.organization}</div>
                  <h3>{cert.title}</h3>

                  <div className="certification-row__tags">
                    {(cert.skills?.length ? cert.skills : ['General engineering']).slice(0, 3).map((skill, skillIndex, skills) => (
                      <span key={skill}>
                        {skill}
                        {skillIndex < skills.length - 1 ? <span className="certification-row__tag-separator">•</span> : null}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="certification-row__side">
                  <span>{cert.organization}</span>
                  <div className="certification-row__date">{formatIssueDate(cert.issueDate)}</div>
                </div>
              </article>
            ))}
          </div>

          {sortedCertifications.length > INITIAL_DISPLAY_COUNT && (
            <div className="certifications-actions">
              {hasMoreCertifications ? (
                <button
                  type="button"
                  className="certifications-action-button certifications-action-button--more"
                  onClick={() => setDisplayCount((prev) => Math.min(prev + DISPLAY_INCREMENT, sortedCertifications.length))}
                >
                  <span>Show more</span>
                  <span className="certifications-action-button__icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9"></polyline>
                    </svg>
                  </span>
                </button>
              ) : null}

              {canShowLess ? (
                <button
                  type="button"
                  className="certifications-action-button certifications-action-button--less"
                  onClick={handleShowLess}
                >
                  <span>Show less</span>
                  <span className="certifications-action-button__icon" aria-hidden="true">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="18 15 12 9 6 15"></polyline>
                    </svg>
                  </span>
                </button>
              ) : null}
            </div>
          )}
        </section>
      </div>

      <div style={{ marginTop: isMobileView ? '96px' : '120px' }}>
        <Footer />
      </div>

      <style>{`
        .certifications-shell {
          max-width: 1180px;
          margin: 0 auto;
        }

        .certifications-kicker {
          display: inline-flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 18px;
          color: rgba(245, 245, 245, 0.5);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .certifications-kicker::before {
          content: '';
          width: 36px;
          height: 1px;
          background: linear-gradient(90deg, rgba(196, 149, 106, 0.95), rgba(196, 149, 106, 0));
        }

        .certifications-hero {
          margin-bottom: 56px;
        }

        .certifications-hero__row {
          display: grid;
          grid-template-columns: minmax(0, 1.35fr) minmax(240px, 0.65fr);
          gap: 32px;
          align-items: end;
          padding-bottom: 30px;
          border-bottom: 1px solid rgba(245, 245, 245, 0.08);
        }

        .certifications-hero__copy h1 {
          max-width: 760px;
          font-size: clamp(40px, 6vw, 72px);
          line-height: 0.98;
          letter-spacing: -0.05em;
          margin-bottom: 16px;
        }

        .certifications-hero__copy p {
          max-width: 620px;
          color: rgba(245, 245, 245, 0.68);
          font-size: clamp(16px, 2vw, 19px);
          line-height: 1.72;
        }

        .certifications-hero__meta {
          display: flex;
          flex-direction: column;
          justify-content: flex-end;
          gap: 26px;
        }

        .certifications-hero__stat,
        .certifications-hero__note {
          padding: 18px 0 0;
          border-top: 1px solid rgba(245, 245, 245, 0.08);
        }

        .certifications-hero__stat span {
          display: block;
          margin-bottom: 6px;
          color: var(--brand-primary);
          font-family: var(--font-heading);
          font-size: clamp(40px, 4.2vw, 64px);
          letter-spacing: -0.04em;
          line-height: 0.95;
        }

        .certifications-hero__stat p {
          color: rgba(245, 245, 245, 0.46);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .certifications-hero__note {
          color: rgba(245, 245, 245, 0.62);
          font-size: 15px;
          line-height: 1.75;
          max-width: 320px;
        }

        .certifications-spotlight {
          display: grid;
          grid-template-columns: minmax(280px, 0.82fr) minmax(0, 1fr);
          gap: 28px;
          align-items: center;
          padding: 24px 0 36px;
          margin-bottom: 64px;
          border-bottom: 1px solid rgba(245, 245, 245, 0.08);
        }

        .certifications-spotlight__image {
          height: 320px;
          overflow: hidden;
          border-radius: 24px;
          background: rgba(245, 245, 245, 0.03);
          border: 1px solid rgba(245, 245, 245, 0.08);
        }

        .certifications-spotlight__image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }

        .certifications-certificate-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(245, 245, 245, 0.52);
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          background:
            radial-gradient(circle at top, rgba(196, 149, 106, 0.12), transparent 40%),
            rgba(245, 245, 245, 0.02);
        }

        .certifications-spotlight__topline,
        .certification-row__date,
        .certification-row__topline,
        .certification-row__side span {
          font-family: var(--font-mono);
          font-size: 11px;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .certifications-spotlight__topline {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-bottom: 14px;
          color: rgba(245, 245, 245, 0.42);
        }

        .certifications-spotlight__topline span:first-child,
        .certification-row__topline {
          color: var(--brand-primary);
        }

        .certifications-spotlight__content h2 {
          font-size: clamp(30px, 4vw, 48px);
          line-height: 1.06;
          letter-spacing: -0.04em;
          margin-bottom: 14px;
        }

        .certifications-spotlight__content p {
          max-width: 620px;
          color: rgba(245, 245, 245, 0.66);
          font-size: 16px;
          line-height: 1.74;
          margin-bottom: 18px;
        }

        .certifications-skill-strip,
        .certification-row__tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .certifications-skill-strip span,
        .certification-row__tags span {
          display: inline-flex;
          align-items: center;
          min-height: 28px;
          padding: 0 10px;
          border-radius: 999px;
          border: 1px solid rgba(196, 149, 106, 0.14);
          background: linear-gradient(180deg, rgba(196, 149, 106, 0.06), rgba(245, 245, 245, 0.02));
          color: rgba(245, 245, 245, 0.72);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
        }

        .certifications-skill-strip span {
          font-family: var(--font-mono);
          font-size: 10px;
          letter-spacing: 0.1em;
          text-transform: uppercase;
        }

        .certification-row__tags span {
          padding: 0;
          min-height: auto;
          border: none;
          border-radius: 0;
          background: transparent;
          box-shadow: none;
          color: #737373;
          font-size: 12px;
          font-weight: 500;
          letter-spacing: 0.05em;
          text-transform: uppercase;
        }

        .certifications-archive__header {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(260px, 0.7fr);
          gap: 24px;
          align-items: end;
          margin-bottom: 30px;
        }

        .certifications-archive__header h2 {
          font-size: clamp(28px, 4vw, 42px);
          line-height: 1.06;
          letter-spacing: -0.04em;
        }

        .certifications-archive__header p {
          max-width: 400px;
          color: rgba(245, 245, 245, 0.58);
          font-size: 15px;
          line-height: 1.72;
        }

        .certifications-list {
          display: flex;
          flex-direction: column;
          border-top: 1px solid rgba(245, 245, 245, 0.06);
        }

        .certification-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 32px;
          padding: 40px 24px;
          border-bottom: 1px solid rgba(245, 245, 245, 0.06);
          position: relative;
          border-radius: 8px;
          margin: 0 -24px;
          transition:
            background-color var(--duration-normal) var(--ease-out),
            transform var(--duration-normal) var(--ease-out);
        }

        .certification-row__date {
          color: #525252;
          font-family: 'JetBrains Mono', monospace;
          font-size: 12px;
          letter-spacing: 0.05em;
          transition: color var(--duration-normal) var(--ease-out);
        }

        .certification-row__body {
          flex: 1;
          max-width: 75%;
        }

        .certification-row__body h3 {
          color: #E5E5E5;
          font-family: 'Space Grotesk', sans-serif;
          font-size: 26px;
          font-weight: 500;
          line-height: 1.3;
          letter-spacing: -0.01em;
          margin: 8px 0 10px;
          transition: color var(--duration-normal) var(--ease-out);
        }

        .certification-row__topline {
          color: var(--brand-primary);
          margin-bottom: 8px;
        }

        .certification-row:hover {
          background: rgba(255, 255, 255, 0.02);
        }

        .certification-row:hover h3 {
          color: var(--brand-primary);
        }

        .certification-row__side {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 4px;
          min-width: 180px;
        }

        .certification-row__side span {
          color: #A3A3A3;
          font-family: 'Inter', sans-serif;
          font-size: 13px;
          font-weight: 500;
          letter-spacing: normal;
          text-transform: none;
          transition: color var(--duration-normal) var(--ease-out);
        }

        .certification-row__tag-separator {
          color: #404040;
          margin: 0 8px;
        }

        .certifications-actions {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-wrap: wrap;
          gap: 16px;
          width: 100%;
          margin-top: 40px;
        }

        .certifications-action-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: auto;
          padding: 0 0 4px;
          position: relative;
          border: none;
          border-radius: 0;
          background: transparent;
          color: rgba(245, 245, 245, 0.84);
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          font-weight: 500;
          letter-spacing: 0.005em;
          line-height: 1;
          cursor: pointer;
          transition:
            transform var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out);
        }

        .certifications-action-button::after {
          content: '';
          position: absolute;
          left: 0;
          right: 0;
          bottom: -2px;
          height: 1px;
          background: rgba(196, 149, 106, 0.75);
          transform: scaleX(0.35);
          transform-origin: center;
          opacity: 0;
          transition:
            transform 220ms var(--ease-out),
            opacity 220ms var(--ease-out);
        }

        .certifications-action-button:hover {
          transform: translateY(-1px);
          color: var(--brand-primary);
        }

        .certifications-action-button:hover::after,
        .certifications-action-button:focus-visible::after {
          transform: scaleX(1);
          opacity: 1;
        }

        .certifications-action-button--more {
          color: rgba(245, 245, 245, 0.9);
        }

        .certifications-action-button--less {
          color: rgba(245, 245, 245, 0.72);
        }

        .certifications-action-button__icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(245, 245, 245, 0.1);
          flex-shrink: 0;
          transition:
            transform var(--duration-normal) var(--ease-out),
            border-color var(--duration-normal) var(--ease-out),
            background var(--duration-normal) var(--ease-out),
            color var(--duration-normal) var(--ease-out);
        }

        .certifications-action-button:hover .certifications-action-button__icon {
          border-color: rgba(196, 149, 106, 0.28);
          background: rgba(196, 149, 106, 0.08);
          color: var(--brand-primary);
        }

        .certifications-action-button:first-child:hover .certifications-action-button__icon {
          transform: translateY(2px);
        }

        .certifications-action-button:last-child:hover .certifications-action-button__icon {
          transform: translateY(-2px);
        }

        .certifications-action-button:focus-visible {
          outline: none;
          color: var(--brand-primary);
        }

        .certification-row:hover .certification-row__side span {
          color: rgba(245, 245, 245, 0.88);
        }

        @media (max-width: 900px) {
          .certifications-hero__row,
          .certifications-spotlight,
          .certifications-archive__header {
            grid-template-columns: 1fr;
          }

          .certification-row {
            flex-direction: column;
            align-items: flex-start;
            gap: 16px;
            padding: 32px 0;
            margin: 0;
            border-radius: 0;
          }

          .certification-row__body {
            max-width: 100%;
          }

          .certification-row__side {
            align-items: flex-start;
            gap: 4px;
            min-width: 100%;
            margin-top: 8px;
          }
        }

        @media (max-width: 768px) {
          .certifications-hero {
            margin-bottom: 40px;
          }

          .certifications-hero__copy h1 {
            font-size: 42px;
          }

          .certifications-spotlight {
            padding: 20px 0 28px;
            margin-bottom: 48px;
          }

          .certifications-spotlight__image {
            height: 220px;
          }

          .certification-row__body h3 {
            font-size: 22px;
          }

          .certifications-actions {
            gap: 10px;
            align-items: flex-start;
          }

          .certifications-action-button {
            width: auto;
            justify-content: flex-start;
          }
        }
      `}</style>
    </section>
  );
};

export default CertificationsPage;
